import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  Checkbox,
  Autocomplete,
  alpha,
  Tabs,
  Tab,
  CircularProgress,
  useTheme,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { LoadingButton } from "@mui/lab";
import { MailOutlined } from "@mui/icons-material";
import { API_SERVICE_BACKEND } from "src/config";

const MailboxAssignDialog = React.memo(function MailboxAssignDialog({
  open,
  onClose,
  tag,       // { _id, name, color, mailboxes: [{mailboxId, senderEmail, connectionStatus}] }
  userEmail,
  onUpdated, // called after successful assign/unassign
}) {
  const theme = useTheme();
  const [allMailboxes, setAllMailboxes] = useState([]);
  const [mailboxesLoading, setMailboxesLoading] = useState(false);
  const [selectedMailboxIds, setSelectedMailboxIds] = useState([]);
  const [mailboxSearch, setMailboxSearch] = useState("");
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [assignedSelectedDomains, setAssignedSelectedDomains] = useState([]);
  const [activeDialogTab, setActiveDialogTab] = useState("assigned");
  const [isUpdatingMailboxes, setIsUpdatingMailboxes] = useState(false);

  useEffect(() => {
    if (!open || !tag) {
      setAllMailboxes([]);
      setMailboxSearch("");
      setSelectedDomains([]);
      setAssignedSelectedDomains([]);
      setIsUpdatingMailboxes(false);
      return;
    }

    const hasAssigned = (tag.mailboxes?.length ?? 0) > 0;
    setActiveDialogTab(hasAssigned ? "assigned" : "notAssigned");

    const assignedMbIds = (tag.mailboxes || []).map((mb) => mb.mailboxId);
    setSelectedMailboxIds(assignedMbIds);

    const allAssignedDomains = [
      ...new Set(
        (tag.mailboxes || []).map((mb) => mb.senderEmail?.split("@")[1] || "Unknown")
      ),
    ].sort();
    setAssignedSelectedDomains(allAssignedDomains);

    const fetchAllMailboxes = async () => {
      try {
        setMailboxesLoading(true);
        const res = await axios.get(`${API_SERVICE_BACKEND}/usermailbox/${userEmail}?limit=1000`);
        setAllMailboxes(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch mailboxes:", err);
      } finally {
        setMailboxesLoading(false);
      }
    };
    fetchAllMailboxes();
  }, [open, tag?._id, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateMailboxes = async () => {
    if (!tag?._id) return;
    const assignedIds = (tag.mailboxes || []).map((mb) => mb.mailboxId);
    const toRemove = assignedIds.filter((id) => !selectedMailboxIds.includes(id));
    const toAdd = selectedMailboxIds.filter((id) => !assignedIds.includes(id));
    if (!toRemove.length && !toAdd.length) return;
    const basePayload = { tag_id: tag._id, tag: tag.name, user: userEmail };
    try {
      setIsUpdatingMailboxes(true);
      await Promise.all(
        [
          toRemove.length &&
            axios.put(`${API_SERVICE_BACKEND}/tags/${tag._id}`, {
              ...basePayload, assign: false, mailbox_ids: toRemove,
            }),
          toAdd.length &&
            axios.put(`${API_SERVICE_BACKEND}/tags/${tag._id}`, {
              ...basePayload, assign: true, mailbox_ids: toAdd,
            }),
        ].filter(Boolean)
      );
      toast.success("Mailboxes updated");
      onClose();
      onUpdated?.();
    } catch (err) {
      console.error("Failed to update mailboxes:", err);
      toast.error("Failed to update mailboxes");
    } finally {
      setIsUpdatingMailboxes(false);
    }
  };

  if (!tag) return null;

  const tColor = tag.color || "#3B82F6";
  const assignedIdSet = new Set((tag.mailboxes || []).map((mb) => mb.mailboxId));
  const q = mailboxSearch.trim().toLowerCase();

  const renderMailboxRow = (id, email, i, total, connectionStatus, isSuspended, warmupStatus) => {
    const isChecked = selectedMailboxIds.includes(id);
    let isWarmingUp;
    if (warmupStatus === "In Progress") {
      isWarmingUp = true;
    } else if (isSuspended?.value === false || isSuspended?.reason === "BY_CLIENT") {
      isWarmingUp = false;
    }
    const statusColor = isWarmingUp ? "#FF9800" : connectionStatus === "CONNECTED" ? "#12B981" : "#E20E0E";
    const statusLabel = isWarmingUp ? "Warming Up" : connectionStatus === "CONNECTED" ? "Connected" : "Disconnected";
    return (
      <Box
        key={id}
        onClick={() => setSelectedMailboxIds((prev) => isChecked ? prev.filter((x) => x !== id) : [...prev, id])}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          px: 2.5, py: 1.25, cursor: "pointer",
          borderBottom: i < total - 1 ? `1px solid ${theme.palette.divider}` : "none",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <Checkbox size="small" checked={isChecked} onChange={() => {}} sx={{ p: 0.5 }} />
        <Box sx={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: alpha(tColor, 0.1), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <MailOutlined sx={{ fontSize: 15, color: tColor }} />
        </Box>
        <Typography sx={{ fontSize: "14px", color: theme.palette.text.primary, flex: 1 }}>{email}</Typography>
        {connectionStatus && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: statusColor, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "12px", color: statusColor, fontWeight: 500 }}>{statusLabel}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderDomainDropdown = (options, groups, value, onChange, placeholder) => (
    <Box sx={{ flex: 1 }}>
      <Autocomplete
        multiple size="small"
        className="glass-dropdown"
        options={options}
        value={value}
        onChange={onChange}
        disableCloseOnSelect
        getOptionLabel={(o) => `@${o}`}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox size="small" checked={selected} sx={{ p: 0.5, mr: 0.5 }} />
            <Typography sx={{ fontSize: "13px" }}>@{option}</Typography>
            <Typography sx={{ fontSize: "12px", color: "text.disabled", ml: 0.75 }}>({groups[option]?.length ?? 0})</Typography>
          </li>
        )}
        renderTags={(val, getTagProps) =>
          val.map((opt, idx) => (
            <Chip {...getTagProps({ index: idx })} key={opt} label={`@${opt}`} size="small" sx={{ fontSize: "12px" }} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length ? "" : placeholder}
            size="small"
            sx={{
              "& input": { color: theme.palette.text.primary },
              "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
            }}
          />
        )}
        sx={{
          "& .MuiInputBase-root": {
            maxHeight: 80, overflowY: "auto", alignItems: "flex-start", pt: "6px !important",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: theme.palette.divider, borderRadius: 2 },
          },
        }}
      />
    </Box>
  );

  const renderSelectAllBar = (rows, idFn, label) => {
    const checkedCount = rows.filter((r) => selectedMailboxIds.includes(idFn(r))).length;
    const allChk = checkedCount === rows.length && rows.length > 0;
    const someChk = checkedCount > 0 && !allChk;
    return (
      <Box sx={{ px: 2.5, py: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.action.hover }}>
        <Typography sx={{ fontSize: "12px", color: theme.palette.text.primary }}>{rows.length} {label}</Typography>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
          onClick={() => setSelectedMailboxIds((prev) =>
            allChk
              ? prev.filter((id) => !rows.find((r) => idFn(r) === id))
              : [...new Set([...prev, ...rows.map(idFn)])]
          )}
        >
          <Checkbox size="small" checked={allChk} indeterminate={someChk} onChange={() => {}} sx={{ p: 0.25 }} />
          <Typography sx={{ fontSize: "12px", color: theme.palette.text.primary, userSelect: "none" }}>Select all</Typography>
        </Box>
      </Box>
    );
  };

  const renderAssignedTab = () => {
    const assignedDomainGroups = {};
    (tag.mailboxes || []).forEach((mb) => {
      const d = mb.senderEmail?.split("@")[1] || "Unknown";
      if (!assignedDomainGroups[d]) assignedDomainGroups[d] = [];
      assignedDomainGroups[d].push(mb);
    });
    const assignedDomainOptions = Object.keys(assignedDomainGroups).sort();
    const filtered = (tag.mailboxes || []).filter((mb) => !q || mb.senderEmail?.toLowerCase().includes(q));

    const handleAssignedDomainChange = (_, newDomains) => {
      const unchecked = assignedSelectedDomains.filter((d) => !newDomains.includes(d));
      const rechecked = newDomains.filter((d) => !assignedSelectedDomains.includes(d));
      setAssignedSelectedDomains(newDomains);
      setSelectedMailboxIds((prev) => {
        let updated = [...prev];
        unchecked.forEach((d) => {
          const ids = (assignedDomainGroups[d] || []).map((mb) => mb.mailboxId);
          updated = updated.filter((id) => !ids.includes(id));
        });
        rechecked.forEach((d) => {
          (assignedDomainGroups[d] || []).forEach((mb) => {
            if (!updated.includes(mb.mailboxId)) updated.push(mb.mailboxId);
          });
        });
        return updated;
      });
    };

    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, pt: 2, pb: 1.5 }}>
          <TextField
            size="small" placeholder="Search assigned mailboxes…"
            className="glass-input"
            value={mailboxSearch} onChange={(e) => setMailboxSearch(e.target.value)}
            sx={{ width: 220, flexShrink: 0, "& input": { color: theme.palette.text.primary }, "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 } }}
          />
          {renderDomainDropdown(assignedDomainOptions, assignedDomainGroups, assignedSelectedDomains, handleAssignedDomainChange, "Remove by domain…")}
        </Box>
        {renderSelectAllBar(filtered, (mb) => mb.mailboxId, "assigned mailboxes")}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>No assigned mailboxes found.</Typography>
            </Box>
          ) : filtered.map((mb, i) => {
            const full = allMailboxes.find((m) => m._id === mb.mailboxId);
            return renderMailboxRow(mb.mailboxId, mb.senderEmail, i, filtered.length, full?.connectionStatus ?? mb.connectionStatus, full?.isSuspended, full?.warmupStatus);
          })}
        </Box>
      </>
    );
  };

  const renderNotAssignedTab = () => {
    const unassignedList = allMailboxes.filter((mb) => !assignedIdSet.has(mb._id) && mb.connectionStatus === "CONNECTED" && mb.type !== "OTHER");
    const unassignedDomainGroups = {};
    unassignedList.forEach((mb) => {
      const d = mb.senderEmail?.split("@")[1] || "Unknown";
      if (!unassignedDomainGroups[d]) unassignedDomainGroups[d] = [];
      unassignedDomainGroups[d].push(mb);
    });
    const unassignedDomainOptions = Object.keys(unassignedDomainGroups).sort();
    const filteredUnassigned = unassignedList.filter((mb) => !q || mb.senderEmail?.toLowerCase().includes(q));

    const handleUnassignedDomainChange = (_, newDomains) => {
      const added = newDomains.filter((d) => !selectedDomains.includes(d));
      const removed = selectedDomains.filter((d) => !newDomains.includes(d));
      setSelectedDomains(newDomains);
      setSelectedMailboxIds((prev) => {
        let updated = [...prev];
        added.forEach((d) => {
          (unassignedDomainGroups[d] || []).forEach((mb) => {
            if (!updated.includes(mb._id)) updated.push(mb._id);
          });
        });
        removed.forEach((d) => {
          const ids = (unassignedDomainGroups[d] || []).map((mb) => mb._id);
          updated = updated.filter((id) => !ids.includes(id));
        });
        return updated;
      });
    };

    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, pt: 2, pb: 1.5 }}>
          <TextField
            size="small" placeholder="Search unassigned mailboxes…"
            className="glass-input"
            value={mailboxSearch} onChange={(e) => setMailboxSearch(e.target.value)}
            sx={{ width: 220, flexShrink: 0, "& input": { color: theme.palette.text.primary }, "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 } }}
          />
          {renderDomainDropdown(unassignedDomainOptions, unassignedDomainGroups, selectedDomains, handleUnassignedDomainChange, "Add by domain…")}
        </Box>
        {renderSelectAllBar(filteredUnassigned, (mb) => mb._id, "unassigned mailboxes")}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {filteredUnassigned.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>No unassigned mailboxes found.</Typography>
            </Box>
          ) : filteredUnassigned.map((mb, i) =>
            renderMailboxRow(mb._id, mb.senderEmail, i, filteredUnassigned.length, mb.connectionStatus, mb.isSuspended, mb.warmupStatus)
          )}
        </Box>
      </>
    );
  };

  const assignedIds = (tag.mailboxes || []).map((mb) => mb.mailboxId);
  const toRemove = assignedIds.filter((id) => !selectedMailboxIds.includes(id));
  const toAdd = selectedMailboxIds.filter((id) => !assignedIds.includes(id));
  const hasChanges = toRemove.length > 0 || toAdd.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, minHeight: "65vh", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", pb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: tColor, flexShrink: 0 }} />
          {tag.name}
        </Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "text.secondary", mt: 0.5 }}>
          {tag.mailboxes?.length ?? 0} mailbox{tag.mailboxes?.length !== 1 ? "es" : ""} currently assigned
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 3 }}>
        <Tabs
          value={activeDialogTab}
          onChange={(_, v) => { setActiveDialogTab(v); setMailboxSearch(""); }}
          sx={{ minHeight: 40 }}
        >
          <Tab
            label={`Assigned (${tag.mailboxes?.length ?? 0})`}
            value="assigned"
            sx={{ minHeight: 40, fontSize: "13px", fontWeight: 600 }}
          />
          <Tab
            label={`Not Assigned (${allMailboxes.filter((mb) => !assignedIdSet.has(mb._id) && mb.connectionStatus === "CONNECTED" && mb.type !== "OTHER").length})`}
            value="notAssigned"
            sx={{ minHeight: 40, fontSize: "13px", fontWeight: 600 }}
          />
        </Tabs>
      </Box>
      <Divider />

      <DialogContent sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {mailboxesLoading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : activeDialogTab === "assigned" ? renderAssignedTab() : renderNotAssignedTab()}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          className="glass-button-secondary-50"
          sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary, fontWeight: 500, borderRadius: 1 }}
        >
          Close
        </Button>
        {hasChanges && (
          <LoadingButton
            variant="contained"
            loading={isUpdatingMailboxes}
            onClick={handleUpdateMailboxes}
            sx={{ fontWeight: 600, borderRadius: 1 }}
          >
            {activeDialogTab === "assigned" ? "Unassign" : "Assign"}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default MailboxAssignDialog;
